import { useTranslation } from 'react-i18next';

import {
    IAttendanceStatusChoices,
    IOfferEventDate,
    IOfferEventDateListItemFragment,
    IOfferEventEnrollmentManageableFragment,
    IOfferEventSubscriptionStatus,
} from 'graphql/types';
import { Chip } from 'common/components/Chip';
import { Tooltip } from 'common/components/Tooltip';
import { getOfferEventSubscriptionStatusData } from 'offer/utils/offerEvent';

interface IProps {
    userAttendance: IOfferEventEnrollmentManageableFragment['attendance'];
    offerEventDates: (IOfferEventDate | IOfferEventDateListItemFragment)[];
}

export const OfferEventAttendanceChips = ({
    userAttendance,
    offerEventDates,
}: IProps) => {
    const [translate] = useTranslation();

    if (!offerEventDates.length || !userAttendance?.length) return null;

    // If there are more dates than attendances we know there are open attendance dates
    const openAmount = offerEventDates.length - userAttendance.length;

    let presentAmount = 0;
    let absentAmount = 0;

    offerEventDates.forEach((offerEventDate) => {
        const attendance = userAttendance.find(
            (attendance) => attendance.offerEventDate?.id === offerEventDate.id
        );

        if (!attendance) return;

        if (attendance.status === IAttendanceStatusChoices.Present) {
            presentAmount++;
        } else {
            absentAmount++;
        }
    });

    let enrollmentStatusData = getOfferEventSubscriptionStatusData(
        IOfferEventSubscriptionStatus.Completed
    );
    let chipLabel = enrollmentStatusData?.label;

    const isAbsent = !!absentAmount && !presentAmount;

    if (isAbsent) {
        enrollmentStatusData = getOfferEventSubscriptionStatusData(
            IOfferEventSubscriptionStatus.Absent
        );
        chipLabel = enrollmentStatusData?.label;
    } else if (offerEventDates.length > 1) {
        const presentAmountPercentage = Math.round(
            (presentAmount / offerEventDates.length) * 100
        );

        chipLabel = translate('enrollmentsTable.presentAmountChip', {
            count: presentAmountPercentage,
        });
    }

    return (
        <>
            {!!openAmount && (
                <Tooltip
                    title={translate('enrollmentsTable.attendanceOpenTooltip')}
                >
                    <Chip
                        bgColor="default"
                        label={translate('enrollmentsTable.attendanceOpen', {
                            count: openAmount,
                        })}
                    />
                </Tooltip>
            )}

            <Chip bgColor={enrollmentStatusData?.color} label={chipLabel} />
        </>
    );
};
